import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import SydButton from '../../../../commonDesign/Button'
import { useSavedQuery } from './SavedQueryProvider'
import FavoriteStarButton from './FavoriteStarButton'
import { DropdownMenu, DropdownMenuItem, DropdownMenuDivider, DropdownMenuShortcut } from './DropdownMenu'

const useStyles = makeStyles(() => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '0 12px',
    height: '100%',
    width: '100%'
  },
  spacer: {
    flex: 1
  },
  nameGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minWidth: 0
  },
  queryName: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--font-color-main)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '300px'
  },
  dirtyIndicator: {
    fontSize: '13px',
    color: 'var(--color-contrast-neutral-300)'
  },
  separator: {
    width: '1px',
    height: '20px',
    backgroundColor: 'var(--border-color)',
    margin: '0 4px'
  },
  menuAnchor: {
    position: 'relative'
  }
}))

function QueryToolbar ({ onNew, onOpen, onSave, onSaveAs, onDelete, onFavorite, onSubmitQuery, processing, saving, favoriting }) {
  const classes = useStyles()
  const { currentQuery, isDirty } = useSavedQuery()
  const [menuOpen, setMenuOpen] = useState(false)

  const closeMenu = useCallback(() => setMenuOpen(false), [])

  const wrap = useCallback((fn) => () => {
    closeMenu()
    if (fn) fn()
  }, [closeMenu])

  const isMac = typeof navigator !== 'undefined' && navigator.platform.includes('Mac')
  const modKey = isMac ? '\u2318' : 'Ctrl+'

  return (
    <div className={classes.toolbar}>
      <SydButton
        size='sm'
        variant='primary'
        onClick={onSubmitQuery}
        processing={processing}
        title={`Execute query (${modKey}Enter)`}
      >
        Go
      </SydButton>

      <div className={classes.separator} />

      <div className={classes.menuAnchor}>
        <SydButton size='sm' variant='ghost' onClick={() => setMenuOpen(prev => !prev)}>
          File
        </SydButton>
        <DropdownMenu open={menuOpen} onClose={closeMenu}>
          <DropdownMenuItem onClick={wrap(onNew)}>
            New
          </DropdownMenuItem>
          <DropdownMenuItem onClick={wrap(onOpen)}>
            Open...
          </DropdownMenuItem>
          <DropdownMenuDivider />
          <DropdownMenuItem onClick={wrap(onSave)} disabled={saving}>
            Save
            <DropdownMenuShortcut>{modKey}S</DropdownMenuShortcut>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={wrap(onSaveAs)} disabled={saving}>
            Save As...
          </DropdownMenuItem>
          {currentQuery && (
            <>
              <DropdownMenuDivider />
              <DropdownMenuItem onClick={wrap(onDelete)} danger>
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenu>
      </div>

      <SydButton size='sm' variant='ghost' onClick={onSave} disabled={saving || (!isDirty && !!currentQuery)} processing={saving}>
        Save
      </SydButton>

      <div className={classes.spacer} />

      <div className={classes.nameGroup}>
        {currentQuery && (
          <FavoriteStarButton
            isFavorite={currentQuery.isFavorite}
            loading={favoriting}
            onClick={onFavorite}
            size='sm'
          />
        )}
        <span className={classes.queryName}>
          {currentQuery ? currentQuery.name : 'Untitled'}
        </span>
        {isDirty && <span className={classes.dirtyIndicator}>(modified)</span>}
      </div>
    </div>
  )
}

QueryToolbar.propTypes = {
  onNew: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onSaveAs: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onFavorite: PropTypes.func.isRequired,
  onSubmitQuery: PropTypes.func.isRequired,
  processing: PropTypes.bool,
  saving: PropTypes.bool,
  favoriting: PropTypes.bool
}

export default QueryToolbar
